import { AlertTriangle, ChevronDown, ChevronUp, MessageSquare, Redo2, Undo2, X } from 'lucide-react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
// The frame takes its strings as props by design; this one is the exception, because the
// condition that raises it is computed HERE and threading it through twelve blocks would be
// twelve chances to thread it differently.
import { useTranslation } from 'react-i18next';

import { AnchoredStylingBar } from './anchored-styling-bar';
import { useRecallBlockedHere } from './answer-ref-context';
import { BlockEditorDrawer } from './block-editor-drawer';
import { BlockNameField } from './block-name-field';
import { TestBar, type TestBarProps } from './collapsed-input-block';
import { FeedbackPanel } from './feedback-panel';
import { registerBlockReveal } from './field-reveal';
import { useBlockIdentity } from './fired-states';
import { COLUMN_WIDTH_MAX, COLUMN_WIDTH_MIN, OPTIONS_PANEL_SINGLE_COLUMN, OPTIONS_PANEL_TWO_COLUMN, TWO_COLUMN_CHROME } from './options-section';
import { RichTextEditor } from './rich-text-editor';

import type { FeedbackPanelProps } from './feedback-panel';
import type { AngleMode } from './formula-eval';
import type { SharedEditorToolbar } from './use-shared-editor-toolbar';
import type { GradingMode } from '@agavi/blocks';

import { Button } from '@/components/ui/button';
import { AngleModeProvider } from '@/contexts/angle-mode-context';
import { usePhoneUndo } from '@/contexts/phone-undo-context';

/** A block that passes no writer gets a reader-only scope: its fields still resolve a mode, they
 *  just cannot change one. Stable so the context value is not rebuilt every render. */
const NO_ANGLE_WRITE = () => {};

export interface InputBlockFrameProps {
  /** This block's per-field angle modes, keyed by `fieldId`. */
  angleModes?: Record<string, AngleMode>;
  /** The block's old block-wide mode, seeding fields that have never been set individually. */
  angleModeFallback?: AngleMode;
  /** MERGED by the caller, never enumerated: a write listing only the changed key deletes the
   *  rest. */
  onAngleModeChange?: (fieldId: string, mode: AngleMode) => void;
  // Titlebar
  icon: React.ReactNode;
  /** Static block-type name (e.g. "Multiple Choice") — used as a fallback label only if
   * blockName is ever empty; the titlebar itself displays/edits blockName. */
  titleLabel: string;
  /** Teacher-facing block name (see block-naming.ts) — editable inline in the titlebar
   * whenever onBlockNameChange is provided; shown as plain text otherwise. */
  /** The block being edited. Required — it keys the settings-drawer reveal, and an undo of a
   * configuration toggle can only find its control if the drawer it lives in can be opened. A
   * block that did not pass this would fall back to pulsing its whole card with no way to tell
   * that something finer was available, so the compiler asks rather than the behaviour degrading
   * quietly. */
  blockId: string;
  blockName: string;
  onBlockNameChange?: (name: string) => void;
  /** Rejects a candidate rename with an inline error instead of committing it. */
  isBlockNameTaken?: (candidate: string) => boolean;
  closeA11yLabel: string;
  onClose: () => void;

  // Editing content — question editor + block-specific inputs
  editingSlot: React.ReactNode;

  /** Shared rich-text toolbar state (from useSharedEditorToolbar) — the frame renders ONE
   * toolbar box, anchored to the top of whichever of editingSlot/FeedbackPanel is currently
   * showing, that always targets whichever RichTextEditor last registered into this object.
   * The block wires its own question editor, option/item rows, and FeedbackCard content
   * editors into it directly (unchanged by this prop) — this just owns where/how the shared
   * toolbar box itself gets rendered and resets it whenever feedback mode toggles. */
  sharedToolbar: SharedEditorToolbar;

  // Sticky answer bar — rendered between editing content and bottom bar, reserved for a
  // block's current-correct-answer summary (e.g. numerical/MCQ's "no correct answer set yet" /
  // resolved-value bar). Pass null/undefined to hide it (e.g. MCQ graded with correct options set).
  answerBarSlot?: React.ReactNode;

  /**
   * Scenario bar — rendered directly ABOVE the answer bar, and unlike it, on BOTH tabs.
   *
   * For a control that changes how the whole editor reads its own data: the variable scenario a
   * formula answer resolves against. It has to outlive the tab switch, because the surface that
   * most needs interpreting — the feedback chips, which are all numbers — is the surface the
   * answer bar is hidden behind. A block with nothing to parameterise passes nothing.
   */
  scenarioBarSlot?: React.ReactNode;

  // Sticky warning/error bar — rendered directly above the bottom bar (below answerBarSlot, if
  // both are present), for setup issues (missing/invalid config, coverage gaps, etc.) that need
  // to stay visible regardless of scroll position. A distinct slot from answerBarSlot rather than
  // folded into it — the two say different things (this block currently grades to X vs. this
  // block isn't fully/correctly set up) and a block may eventually want both at once. Pass
  // null/undefined to hide it.
  warningBarSlot?: React.ReactNode;

  // Settings drawer — block-specific options; omit to hide the drawer toggle
  settingsSlot?: React.ReactNode;
  settingsA11yLabel?: string;
  /** Visible label for the landscape-layout rail's Options button — `settingsA11yLabel` is aria-only
   * phrasing ("Toggle X options") written for a screen reader, not visible text next to "Has
   * Correct"/"Feedback" on the button itself. Falls back to `settingsA11yLabel` if omitted, but a
   * block opting into `landscapeLayout` should pass a short, plain label (e.g. "Options"). */
  settingsLabel?: string;

  // Bottom bar — grading mode
  gradingMode: GradingMode;
  gradingModeLabel: string;
  GradingModeIcon: React.ElementType;
  gradingModeCycleA11y: string;
  onGradingModeCycle: () => void;

  // Bottom bar — feedback button
  feedbackLabel: string;

  // Feedback panel (gradingMode is taken from the frame's own prop)
  //
  // `beforeSlot` is NOT forwarded to FeedbackPanel — the frame renders it itself, inside the same
  // sticky box as the shared toolbar. Both used to be `sticky top-0 z-10` in this component's one
  // scrollport with no offset between them, so they pinned to the same coordinate and the block's
  // own bar (numerical/slider's variable-test controller, the branching blocks' branch scroller)
  // read as unpinned. Stacking them in ONE sticky box makes them pin as a unit, with no height
  // measurement to keep in sync.
  feedbackPanelProps: Omit<FeedbackPanelProps, 'gradingMode'> & {
    /** Block-specific bar pinned above the feedback list — e.g. numerical/slider's variable test
     * controller, the branching blocks' branch scroller. Rendered by the frame, not the panel. */
    beforeSlot?: React.ReactNode;
  };

  // Optional external control / notification
  defaultIsEditingFeedback?: boolean;
  onFeedbackModeChange?: (isEditing: boolean) => void;

  /** Render the editor as a full-screen portal overlay (phone). */
  fullScreen?: boolean;

  /** Phone-landscape-only 3-column edit layout (left: toolbar rail, middle: question + answer
   * setup + warning/answer bars, right: grading-mode/options/feedback controls), replacing the
   * portrait vertical stack — width is the abundant axis in landscape, height the scarce one, so
   * everything that was a horizontal row becomes a column instead. Only takes effect when
   * `fullScreen` is also true and `phoneMode` isn't `'test'` (a live test run stays full-width,
   * same as today — there's no toolbar/settings/grading-mode relevance during it). Omit (or pass
   * false) for a block that hasn't opted in yet — renders through the exact existing vertical-stack
   * path, unchanged. Pilot: Dropdown only; not yet ported to other blocks. */
  landscapeLayout?: boolean;

  /** Transient, block-scoped error toasts (a failed sensor capture, an unparseable pasted
   * coordinate). Rendered anchored to the BOTTOM OF THIS PANEL rather than the viewport, so the
   * message appears next to the field that raised it instead of at the bottom of the whole task
   * — pass `anchored` on the Toast itself to match. Persistent setup defects belong on
   * `warningBarSlot`, not here. */
  toastSlot?: React.ReactNode;

  // Phone-only "Edit / Test" toggle — on desktop, the canvas card (a separate mount) already
  // shows the live interactive test widget side-by-side with this editor, so there's nothing to
  // toggle to; on phone, this editor fully covers that canvas card, and until now there was no
  // route back to it at all. All props below must be provided together for the toggle to appear
  // (and only while `fullScreen` besides) — omit them entirely for a block that hasn't wired up
  // phone testing yet, which behaves exactly as before. `testSlot` is the block-specific
  // interactive preview (mirrors `children` in `CollapsedInputBlock`'s own isSelected case);
  // `testBar` is the exact same shape already built for that desktop case, rendered here via the
  // same shared `TestBar` component. `phoneMode`/`onPhoneModeChange` are controlled (owned by the
  // caller, not this component) specifically so the block's own "jump to editing this branch"
  // action (built into `testBar.topNotices` by the caller) can flip straight back to 'edit' mode
  // itself — this component has no other channel to expose that.
  testSlot?: React.ReactNode;
  // `gradingMode` omitted and injected below, the same way `feedbackPanelProps` handles it: the
  // frame already owns the mode, so eleven blocks should not each thread it through a second time.
  testBar?: Omit<TestBarProps, 'gradingMode'>;
  editModeLabel?: string;
  testModeLabel?: string;
  phoneMode?: 'edit' | 'test';
  onPhoneModeChange?: (mode: 'edit' | 'test') => void;
}

interface PhoneModeToggleProps {
  phoneMode: 'edit' | 'test';
  onPhoneModeChange?: (mode: 'edit' | 'test') => void;
  editModeLabel?: string;
  testModeLabel?: string;
  onSwitchToTest: () => void;
  /** Tighter horizontal padding to fit the w-40 landscape rails; the standalone portrait row uses
   * the wider default. */
  compact?: boolean;
}

/** Edit/Test segmented control — shared by the standalone full-width row (portrait / blocks not
 * yet opted into landscapeLayout), both landscape rails (Edit rail, Test rail), and the display
 * blocks, which build their own full-screen editor rather than using this frame and would
 * otherwise have had a fourth copy of it. Same track-plus-raised-pill language as
 * SegmentedControl (segmented-control.tsx) — a bordered track with a solid, shadowed active pill
 * reads clearly at a glance without needing a stronger color. */
export function PhoneModeToggle({ phoneMode, onPhoneModeChange, editModeLabel, testModeLabel, onSwitchToTest, compact }: PhoneModeToggleProps) {
  return (
    <div role='group' className='flex gap-0.5 overflow-hidden rounded-lg border border-ws-border bg-ws-btn p-0.5'>
      {(['edit', 'test'] as const).map(mode => (
        <button
          key={mode}
          type='button'
          onClick={() => { onPhoneModeChange?.(mode); if (mode === 'test') onSwitchToTest(); }}
          aria-pressed={phoneMode === mode}
          className={`flex-1 rounded-md ${compact ? 'px-2' : 'px-3'} py-1.5 text-sm font-medium transition-colors ${
            phoneMode === mode
              ? 'bg-ws-surface text-ws-text shadow-sm'
              : 'text-ws-muted hover:text-ws-text'
          }`}
        >
          {mode === 'edit' ? editModeLabel : testModeLabel}
        </button>
      ))}
    </div>
  );
}

/**
 * The rule-and-heading that divides one authoring phase from the next inside `editingSlot`.
 *
 * The frame hands each block a single opaque editing slot, so all eleven hand-built this boundary
 * and it drifted in every way it could: three blocks (dropdown, mcq, keyword) drew no line at all,
 * FITB's and Scale's lived inside one arm of a ternary and so blinked in and out as the teacher
 * authored, and the branching toggle landed above the line in two blocks and below it in two
 * others. Slider's own comment justified its heading as needed "unlike MCQ/Numerical's answer UI",
 * by which time Numerical had one too — the rationale had outlived the code.
 *
 * Deliberately takes a resolved label rather than a key: Slider uses it twice, once for its range
 * setup and once for Answer Setup, and the second use is the reason this is not named for the
 * Answer Setup section it most often introduces.
 *
 * Render it unconditionally. A boundary that appears only in some states is worse than none,
 * because its absence then reads as meaning something.
 */
export function EditorSectionHeading({ label, action, divider = true, sticky = false, id }: {
  /** A node, not just a string, so a heading can carry a set-back qualifier after its label —
   *  the variables panel's "(all tasks)" / "(this task)", which must not read at the heading's
   *  own weight. */
  label: React.ReactNode;
  /** Put on the label element, for a `<section aria-labelledby>` that names this heading. */
  id?: string;
  /**
   * A control belonging to this section, sitting at the end of the heading row.
   *
   * For something a teacher needs available but should not be led to — the question-version
   * pager is the first. Putting it here rather than above the section's first field is what
   * makes it read as furniture of the heading instead of as a feature being advertised.
   */
  action?: React.ReactNode;
  /**
   * The rule above the label, which exists to SEPARATE this section from the one before it.
   *
   * Off for the first heading in an editor, where there is nothing above to separate from and the
   * line just draws a box around the top of the panel.
   */
  divider?: boolean;
  /**
   * Pin the heading to the top of the scrolling panel it lives in.
   *
   * OPT-IN, because it is right in one place and wrong in most: a block editor's QUESTION and
   * ANSWER SETUP headings sit in a short column where pinning would only steal height. The
   * variables panel is the case it exists for — two scopes in one long scroll, where the heading
   * answers "which list am I in" and carries the Reroll button that a teacher wants without
   * scrolling back up for it.
   *
   * A sticky element is held inside its own containing block, so each section's heading gives way
   * as the next one arrives rather than stacking up.
   */
  sticky?: boolean;
}) {
  /** The heading itself, written once: both branches below only wrap it differently. */
  const row = (
    <div className='flex min-h-7 items-center justify-between gap-2'>
      {/* NEVER WRAPS. The action beside it can be wide — the Varies-with link carries a task
          title and a block name — and with the label free to shrink, "VARIES WITH" broke onto
          two lines in the narrow panel and on both phone layouts. The trigger already has
          `min-w-0` and truncates its text, so it is the one that should absorb the squeeze;
          the heading just had to stop volunteering. */}
      <p id={id} className='shrink-0 whitespace-nowrap text-sm font-semibold uppercase tracking-wide text-ws-primary'>{label}</p>
      {action}
    </div>
  );

  if (!sticky) return <div className={divider ? 'border-t border-ws-border pt-3' : ''}>{row}</div>;

  return (
    /*
     * The background is not optional once pinned: rows scroll UNDER this, and a transparent
     * heading would have them reading straight through the text.
     *
     * `pt-4` is the section's top spacing, moved OFF the scrolling body and onto this.
     *
     * It has to live here, not there: a sticky element pins below its scroll container's own top
     * padding, so a body with `pt-4` leaves a 16px strip above the pinned heading where rows
     * stay visible as they pass under. Negative margin does not help — it moves the element in
     * layout without moving where `top: 0` resolves. The padding only covers that strip if it
     * belongs to the thing doing the covering.
     *
     * So a scrolling parent that opts into this must not set its own top padding.
     *
     * AND IT NEVER DRAWS THE DIVIDER. A rule above a heading exists to separate it from the
     * section before it; pinned to the top of the panel there is nothing above it to separate
     * from, and the rule reads as an underline on the panel's own header. A sticky heading's
     * caller puts the rule on the SECTION instead, where it scrolls away with the content it
     * actually divides.
     */
    <div className='sticky top-0 z-10 bg-ws-surface pb-2 pt-4'>{row}</div>
  );
}

/**
 * Forget that the options drawer was open.
 *
 * Called when the teacher CHOOSES a block — clicking one in the list, or adding one. Opening a
 * block should show it as it is, not as the last block's drawer left it: the memory below exists
 * to survive the remount an undo performs, not to make the drawer sticky across ordinary
 * navigation. An undo that needs the drawer opens it itself, through the reveal.
 */
export function forgetSettingsOpen(): void {
  rememberedSettingsOpen = false;
}

/**
 * Whether the options drawer is open — remembered for the lifetime of the page, or until the
 * teacher picks a different block.
 *
 * Not per block, deliberately. Stepping through an undo stack moves between blocks, and a drawer
 * remembered per block shut itself every time the stack crossed into a block whose drawer had not
 * been opened, then reopened as the reveal found the field there — which read as the options panel
 * flapping on every other undo. Within one run of a stack, "the options are open" is one state.
 *
 * Module-level rather than state. It was written when undo REMOUNTED the panel on purpose, which
 * local state could not survive; the remount is gone (every block re-reads its own data now), but
 * this is still the right shape — the value belongs to the RUN of undos, not to any one mounted
 * panel, and it has to hold while the selected block changes underneath it.
 */
let rememberedSettingsOpen = false;

export function InputBlockFrame({
  blockId,
  icon, titleLabel, blockName, onBlockNameChange, isBlockNameTaken, closeA11yLabel, onClose,
  editingSlot,
  sharedToolbar,
  answerBarSlot,
  scenarioBarSlot,
  warningBarSlot,
  settingsSlot, settingsA11yLabel, settingsLabel,
  gradingMode, gradingModeLabel, GradingModeIcon, gradingModeCycleA11y, onGradingModeCycle,
  feedbackLabel,
  feedbackPanelProps,
  defaultIsEditingFeedback = false,
  onFeedbackModeChange,
  fullScreen = false,
  landscapeLayout = false,
  toastSlot,
  testSlot, testBar, editModeLabel, testModeLabel, phoneMode = 'edit', onPhoneModeChange,
  angleModes, angleModeFallback, onAngleModeChange,
}: InputBlockFrameProps) {
  // Split out so the frame can pin it alongside the toolbar (see the prop's own doc comment); the
  // rest goes to FeedbackPanel untouched.
  const { beforeSlot: feedbackBeforeSlot, ...panelProps } = feedbackPanelProps;
  const [isEditingFeedback, setIsEditingFeedback] = useState(defaultIsEditingFeedback);
  /**
   * Seeded from whether the options drawer was last open.
   *
   * Written when undo and redo REMOUNTED this component on purpose, which local state could not
   * survive: an undo that opened the drawer to show a restored setting had it slammed shut by the
   * next undo's remount, and the product showed the options panel popping open and immediately
   * closing. The remount is gone, but the seeding stays — selecting a different block still
   * rebuilds this component, and the drawer belongs to the run rather than to the mount.
   *
   * Feedback is seeded the same way, from `blockFeedbackModes` on the page. This is kept next to
   * the state it describes rather than threaded as a prop through all eleven blocks — the drawer
   * is one boolean and no other component needs to read it.
   */
  const [settingsOpen, setSettingsOpen] = useState(() => rememberedSettingsOpen);
  useEffect(() => { rememberedSettingsOpen = settingsOpen; }, [settingsOpen]);

  /**
   * Let an undo open this drawer.
   *
   * The drawer's contents are portalled into the document only while it is open, so a toggle an
   * undo just took back is not off-screen, it is absent — `pulseFirstField` cannot find it by any
   * amount of scrolling or paging. Registering the opener here, where `settingsOpen` actually
   * lives, is what lets the reveal ladder try the drawer as a last resort before giving up and
   * pulsing the whole block.
   *
   * Reads through a ref so the registration does not churn on every open/close: the registry
   * would otherwise be rewritten twice per drawer interaction for no gain.
   */
  const settingsOpenRef = useRef(settingsOpen);
  settingsOpenRef.current = settingsOpen;
  const feedbackOpenRef = useRef(isEditingFeedback);
  feedbackOpenRef.current = isEditingFeedback;
  // Held in a ref because `setFeedbackMode` closes over props that change every render, while the
  // registration below is keyed only on the block id.
  const setFeedbackModeRef = useRef<(next: boolean) => void>(() => {});
  useEffect(() => registerBlockReveal(blockId, {
    openFeedback: () => setFeedbackModeRef.current(true),
    closeFeedback: () => setFeedbackModeRef.current(false),
    isFeedbackOpen: () => feedbackOpenRef.current,
    openSettings: () => setSettingsOpen(true),
    closeSettings: () => setSettingsOpen(false),
    isSettingsOpen: () => settingsOpenRef.current,
  }), [blockId]);
  const phoneUndo = usePhoneUndo();
  const settingsDrawerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const settingsToggleRef = useRef<HTMLButtonElement>(null);

  /*
   * THE WARNING SITS ON THE CONTROL THAT CAUSED IT.
   *
   * Making a recalled answer optional breaks every chip that reads it, and the only notice
   * was in the issues panel down by the test bar — "not in a good place", a surface about
   * trying the block rather than configuring it, nowhere near the button that did it.
   *
   * ONLY when this button is the culprit. The same block can be unrecallable because someone
   * put an image on an option, and a grading-mode button wearing a warning about that would
   * send the teacher to the wrong control. The issues panel still carries the full sentence
   * for every cause; this is the pointer to the one control that can undo it.
   */
  const blockedRecall = useRecallBlockedHere(useBlockIdentity()?.blockId);
  const recallWarning = blockedRecall?.reason === 'optional' ? (
    <>
      <AlertTriangle aria-hidden='true' className='h-3.5 w-3.5 shrink-0 text-ws-warning-fg' />
      <span className='sr-only'>
        {t('blocks.answerRef.recalledOptionalWarning', { count: blockedRecall.chips })}
      </span>
    </>
  ) : null;
  const frameRef = useRef<HTMLDivElement>(null);
  const hasPhoneTestToggle = fullScreen && !!testSlot && !!testBar && !!editModeLabel && !!testModeLabel;

  // Landscape layout's Options flyout position — computed from the trigger button rather than
  // rendered via plain `absolute` positioning, because every ancestor column in this layout is its
  // own `overflow-y-auto` scroller. Setting overflow on only one axis forces the browser to clip
  // the other axis too (there's no way to allow horizontal overflow while clipping vertically on
  // the same element) — confirmed live: a plain `absolute` flyout got silently clipped by the right
  // rail's own overflow-y-auto the moment it extended past the rail's width. `createPortal` to
  // document.body with `position: fixed`, positioned from `getBoundingClientRect()`, is the same
  // established escape-hatch `FormulaField`'s variable-insertion picker already uses for the exact
  // same reason.
  // Opens downward (top-anchored) or upward (bottom-anchored), whichever side has more room —
  // the Options trigger now sits near the bottom of the rail (grouped with the grading-mode
  // cycle, per the user's requested reorder), so a fixed "always open downward" would push most
  // of the panel below the viewport (confirmed live: the flyout's own bottom edge landed well
  // past a 390px-tall viewport). Same flip heuristic any ordinary popover library uses: compare
  // space above vs. below the trigger, anchor from whichever side has more of it.
  //
  // CAPPED TO THE SPACE IT WAS GIVEN, and that cap is not optional.
  //
  // The flyout's height was whatever its content came to, and the panel is `position: fixed`, so
  // once it was taller than the space on its anchored side the overflow left the viewport with no
  // way to reach it — page scroll cannot move a fixed element, and the panel had no scroller of
  // its own. Measured at 667x375 (phone landscape): Numerical's panel came to 424px against 275px
  // of room and lost its entire first section — integer-only, scientific notation, significant
  // figures — off the top of the screen. Slider lost Two handles and Linear/Logarithmic the same
  // way, Compass lost Magnetic/True north, and GPS fitted with 6px to spare, which is luck rather
  // than a design. Portrait never had the problem: there the same `settingsSlot` renders as
  // `BlockEditorDrawer`'s inline push-down inside a column that scrolls.
  //
  // So the cap travels WITH the position, computed from the same rect: whichever side we anchor
  // to, the panel may use the room on that side and no more, and `overflow-y: auto` below turns
  // the remainder into a scroll rather than a loss. `EDGE_GAP` keeps it off the viewport edge so
  // a clipped panel still reads as clipped.
  const [settingsFlyoutPos, setSettingsFlyoutPos] = useState<{ top: number; right: number; maxHeight: number; maxWidth: number } | { bottom: number; right: number; maxHeight: number; maxWidth: number } | null>(null);
  useEffect(() => {
    if (!settingsOpen) { setSettingsFlyoutPos(null); return; }
    const rect = settingsToggleRef.current?.getBoundingClientRect();
    if (!rect) return;
    const EDGE_GAP = 8;
    const right = window.innerWidth - rect.right;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    // The panel grows LEFTWARD from its right edge, so the room it has is everything to the left
    // of that edge. Measured rather than assumed: the rail's width is not a constant.
    const maxWidth = Math.max(0, rect.right - EDGE_GAP);
    setSettingsFlyoutPos(
      spaceBelow >= spaceAbove
        // `TRIGGER_GAP` is the 8px between the trigger and the panel; the cap spends what is left
        // after it, so the two can never disagree about where the panel starts.
        ? { top: rect.bottom + 8, right, maxHeight: Math.max(0, spaceBelow - 8 - EDGE_GAP), maxWidth }
        : { bottom: window.innerHeight - rect.top + 8, right, maxHeight: Math.max(0, spaceAbove - 8 - EDGE_GAP), maxWidth }
    );
  }, [settingsOpen]);

  /**
   * TWO COLUMNS, BUT ONLY WHEN ONE WILL NOT DO.
   *
   * Landscape has width to spare and no height: the panel sits in 651px of room using 288px of
   * it, against roughly 275px of vertical space. So a panel that would scroll is turned on its
   * side instead — measured live, Numerical's 440px became 244px, Slider's 387px became 241px and
   * Compass's 378px became 243px, all of which fit with nothing hidden.
   *
   * Decided by MEASUREMENT rather than by counting sections, because what overflows depends on
   * how the rows wrapped, and that depends on the viewport. The panel renders in one column, this
   * reads whether it overflowed, and flips.
   *
   * ONE WAY ONLY, per opening. In two columns the panel no longer overflows, which is the
   * condition that put it there — re-evaluating would flip it straight back and then forth
   * forever. `settingsOpen` going false resets it, so the next opening measures afresh.
   *
   * A panel still too tall in two columns keeps the scroll from `maxHeight` above. Two columns is
   * the improvement; the scroller is the guarantee.
   */
  const [settingsTwoColumn, setSettingsTwoColumn] = useState<{ width: number } | null>(null);
  // Read by the measuring loop below, which runs before the state it feeds has been rendered.
  const maxWidthRef = useRef(Number.MAX_SAFE_INTEGER);
  maxWidthRef.current = settingsFlyoutPos?.maxWidth ?? Number.MAX_SAFE_INTEGER;
  useLayoutEffect(() => {
    if (!settingsOpen) { setSettingsTwoColumn(null); return; }
    if (settingsTwoColumn) return;
    const el = settingsDrawerRef.current;
    const inner = el?.firstElementChild as HTMLElement | undefined;
    if (!el || !inner) return;
    // A one-section panel has nothing to break between, so two columns would only split its rows
    // from their own heading — the exact failure `OptionsSection` exists to prevent.
    if (el.querySelectorAll('[data-options-section]').length < 2) return;
    if (el.scrollHeight <= el.clientHeight + 1) return;

    /*
     * HOW WIDE A COLUMN WANTS TO BE, measured rather than fixed.
     *
     * Holding every column at the full 256 made the short panels mostly empty — GPS's rows are
     * "Live reading", "Capture", "Capture on submit". So the panel is briefly laid out at
     * `max-content`, which is the width its longest row needs before wrapping, and that is
     * clamped into the range the panel may use. Numerical's longest row is past the maximum and
     * clamps to it, so the one panel that genuinely needs the full measure still gets it.
     *
     * Read inside a LAYOUT effect, so the measuring width is set and removed between React's
     * mutation and the browser's paint: nothing is ever shown at `max-content`.
     */
    const prev = el.style.width;
    el.style.width = 'max-content';
    const natural = inner.getBoundingClientRect().width;
    el.style.width = prev;
    const from = Math.min(COLUMN_WIDTH_MAX, Math.max(COLUMN_WIDTH_MIN, Math.ceil(natural)));

    /*
     * Then WIDEN until it stops scrolling, rather than taking the narrow width on trust.
     *
     * Narrow columns are taller columns, and measured live the content width alone put a scroll
     * back on Compass (0 -> 15px) and deepened Slider's (11 -> 47px). Trading "all of it on one
     * screen" for "no empty space" gives up the thing two columns were for. So the narrow width
     * is the STARTING point and the loop stops at the first width that fits.
     *
     * Each step applies a width and reads back — synchronous reflows, but inside a layout effect
     * on a panel of a dozen rows, and at most seven of them.
     */
    // The class list is REPLACED, not added to: one-column sets `flex` and two-column sets
    // `block`, and which display wins is decided by stylesheet order rather than by the order
    // they were added. Leaving both on measured a panel still in one column, so every width
    // looked too tall and the loop ran to the maximum every time.
    const innerClass = inner.className;
    inner.className = OPTIONS_PANEL_TWO_COLUMN;
    let column = COLUMN_WIDTH_MAX;
    for (let w = from; w <= COLUMN_WIDTH_MAX; w += 16) {
      el.style.width = `${Math.min(w * 2 + TWO_COLUMN_CHROME, maxWidthRef.current)}px`;
      if (el.scrollHeight <= el.clientHeight + 1) { column = w; break; }
    }
    inner.className = innerClass;
    el.style.width = prev;
    setSettingsTwoColumn({ width: column * 2 + TWO_COLUMN_CHROME });
  }, [settingsOpen, settingsTwoColumn, settingsFlyoutPos]);

  // Traps Tab focus inside the portal while it covers the screen — this is rendered via
  // `createPortal(frame, document.body)` below, so without this, Tab/Shift+Tab walks straight
  // into whatever's behind it in DOM order (the block-list toolbar, other blocks, etc.), which a
  // sighted mouse user would never notice but strands anyone navigating by keyboard/screen reader
  // outside the only content they can actually see.
  useEffect(() => {
    if (!fullScreen) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Tab' || !frameRef.current) return;
      const focusable = frameRef.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [fullScreen]);

  function toggleFeedback() {
    setFeedbackMode(!isEditingFeedback);
  }

  /**
   * The one way the feedback panel opens or shuts.
   *
   * Notifying the page is not optional. The page holds `blockFeedbackModes` and seeds this
   * component's initial state from it, so a reveal that flipped only the local state would be lost
   * the next time this component is built — which used to be every undo, and is now every time a
   * different block is selected. Either way the panel opens and then shuts again, seeded from a
   * page value nobody updated.
   */
  function setFeedbackMode(next: boolean) {
    setIsEditingFeedback(next);
    // The shared toolbar never carries over across this toggle — the editor it was last
    // targeting belongs to whichever view (editing vs. feedback) is about to unmount.
    sharedToolbar.activeEditorRef.current = null;
    sharedToolbar.setActiveEditor(null);
    onFeedbackModeChange?.(next);
  }
  setFeedbackModeRef.current = setFeedbackMode;

  // Closes the settings drawer on any click outside it (and outside its own toggle button,
  // which already handles closing on click without this) — the chevron toggle was the only
  // way to close it before, which felt sticky for a lightweight options panel.
  //
  // Escape also closes it, from anywhere in the document — not just while focus sits inside the
  // portaled flyout. Confirmed live: the landscape layout's Options flyout never moves focus into
  // itself on open (unlike the color/symbols/variable pickers in rich-text-editor.tsx, which do),
  // so focus normally stays right on the toggle button — a per-element onKeyDown on the flyout's
  // own div would never see that keystroke at all, since the toggle button lives in a completely
  // separate DOM subtree (the frame) from the portaled flyout (document.body), so the event never
  // bubbles through it. A document-level listener, exactly like the click-outside handler right
  // above, is the only way to catch Escape regardless of where focus happens to be.
  useEffect(() => {
    if (!settingsOpen) return;

    /**
     * Dismiss-on-outside-click belongs to the FLYOUT, not the inline drawer.
     *
     * In `fullScreen && landscapeLayout` the options are a floating popover over the editor, and
     * clicking away to dismiss it is what a popover is expected to do. Everywhere else they are an
     * inline section that pushes the editor down, and dismissing that on any click in the document
     * means the drawer collapses when you click Undo, or another block, or anything at all — after
     * which the reveal opens it again to show what changed. Reported as the options panel flapping
     * open and shut on every other undo.
     *
     * Escape still closes it in both, which is the keyboard equivalent of the toggle and is never
     * something a teacher presses by accident while doing something else.
     */
    const dismissOnOutsideClick = fullScreen && landscapeLayout;

    function handleClickOutside(e: MouseEvent) {
      if (!dismissOnOutsideClick) return;
      if (settingsDrawerRef.current && !settingsDrawerRef.current.contains(e.target as Node) &&
          settingsToggleRef.current && !settingsToggleRef.current.contains(e.target as Node)) {
        setSettingsOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setSettingsOpen(false);
        requestAnimationFrame(() => settingsToggleRef.current?.focus());
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [settingsOpen, fullScreen, landscapeLayout]);

  const frame = (
    <div
      ref={frameRef}
      /* WHOSE FIELDS THESE ARE. Option and answer ids are NOT unique across a task — `primary`,
         `default-option-1` and `default-item-1` are literal constants minted by whatever creates a
         default block, so two MCQs in one task carry the same option ids. `findAnywhere` resolves a
         field with `document.querySelector`, which returns the first match in the document, so an
         undo in the second block could ring a row in the first. This marks the owner; the settings
         drawer carries the same attribute because it is portalled out of here. */
      data-reveal-block={blockId}
      role={fullScreen ? 'dialog' : undefined}
      aria-modal={fullScreen ? true : undefined}
      aria-label={fullScreen ? blockName || titleLabel : undefined}
      // `relative` is load-bearing for toastSlot below: an anchored Toast is `absolute`, so it
      // resolves against the nearest positioned ancestor. Without this it would escape the panel
      // and land against the viewport, which is the behaviour being fixed. (The fullScreen branch
      // is already `fixed`, hence positioned, but stating it once keeps both branches honest.)
      // `min-h-0` so this panel can be SHORTER than its own content.
      //
      // It is a flex item of the block card, and a flex item defaults to `min-height: auto` — its
      // content height. So `md:h-full` set the panel to the card's height and the default let it
      // overflow anyway; the card is itself `overflow-y-auto`, so it absorbed the difference by
      // scrolling AS A WHOLE. Titlebar, toolbar, scenario bar, bottom bar and all: grab the
      // scrollbar at the card's edge and the entire editor slid off the top, which is not scrolling
      // a panel, it is scrolling the furniture.
      //
      // With this, the panel is pinned to the card's height and the one child that is meant to
      // scroll — the `min-h-0 flex-1 overflow-y-auto` region below — shrinks and takes the overflow,
      // which is what every bar in here was written assuming.
      className={fullScreen
        ? 'fixed inset-x-0 top-0 z-[100] flex flex-col h-[100dvh] bg-ws-surface'
        : 'relative flex min-h-0 flex-col overflow-hidden rounded-xl border border-ws-border bg-ws-surface md:h-full'
      }>
      {/* Titlebar */}
      <div className={`flex items-center gap-2 border-b border-ws-border bg-ws-hover px-4 py-2.5 text-base font-semibold text-ws-primary dark:text-ws-text-link${fullScreen ? '' : ' rounded-t-xl'}`}>
        {icon}
        <BlockNameField
          blockName={blockName}
          titleLabel={titleLabel}
          onBlockNameChange={onBlockNameChange}
          isBlockNameTaken={isBlockNameTaken}
        />
        {fullScreen && (
          <>
            <button
              type='button'
              onClick={phoneUndo.onUndo}
              disabled={!phoneUndo.canUndo}
              aria-label={t('common.undo')}
              className='flex h-7 w-7 items-center justify-center rounded-md hover:bg-black/10 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed'
            >
              <Undo2 aria-hidden='true' className='h-5 w-5' />
            </button>
            <button
              type='button'
              onClick={phoneUndo.onRedo}
              disabled={!phoneUndo.canRedo}
              aria-label={t('common.redo')}
              className='flex h-7 w-7 items-center justify-center rounded-md hover:bg-black/10 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed'
            >
              <Redo2 aria-hidden='true' className='h-5 w-5' />
            </button>
          </>
        )}
        <button
          type='button'
          onClick={onClose}
          aria-label={closeA11yLabel}
          // Extra margin beyond the row's own gap-2 — Undo/Redo are a matched, low-risk pair
          // (redoing an accidental undo is trivially recoverable), but a mis-tap landing on Close
          // instead of Redo dismisses the editor, so Close gets doubled separation from its
          // neighbor rather than sharing the same tight gap Undo/Redo have from each other.
          className='ml-2 flex h-7 w-7 items-center justify-center rounded-md hover:bg-black/10 dark:hover:bg-white/10'
        >
          <X aria-hidden='true' className='h-5 w-5' />
        </button>
      </div>

      {/* Edit/Test toggle — phone only, and only once a block has wired up both testSlot and
          testBar (see their own doc comments on InputBlockFrameProps). Switching to Test also
          drops out of feedback-editing mode — the two don't make sense shown at once, and there's
          no ambiguity to preserve since feedback mode isn't reachable from inside Test anyway.
          In the landscape layout, this moves into whichever rail is active instead (Edit rail or
          Test rail, below) to reclaim the vertical space this row costs — so it's hidden
          entirely whenever landscapeLayout is on, in both modes. */}
      {hasPhoneTestToggle && !(fullScreen && landscapeLayout) && (
        <div className='border-b border-ws-border bg-ws-surface px-4 py-2'>
          <PhoneModeToggle
            phoneMode={phoneMode}
            onPhoneModeChange={onPhoneModeChange}
            editModeLabel={editModeLabel}
            testModeLabel={testModeLabel}
            onSwitchToTest={() => setIsEditingFeedback(false)}
          />
        </div>
      )}

      {hasPhoneTestToggle && phoneMode === 'test' && fullScreen && landscapeLayout ? (
        // Landscape Test mode gets its own rail too, mirroring the Edit rail — testSlot fills the
        // remaining width instead of being squeezed above TestBar's own natural height. TestBar is
        // split across two locations (renderSection — see its own doc comment) rather than
        // stacked whole in the rail: the continuous/submit/reroll-reset controls and topNotices'
        // equation text need width a ~144px rail can't give them (confirmed live: the submit
        // button was pushed off-screen and equations didn't fit), so they move to their own
        // full-width bar below both columns; only the chip legend — which already reads fine
        // narrow — stays in the rail alongside the Edit/Test toggle.
        <>
          <div className='flex flex-1 overflow-hidden'>
            <div className='flex-1 overflow-y-auto p-4'>
              {testSlot}
            </div>
            <div className='flex w-40 shrink-0 flex-col overflow-hidden border-l border-ws-border bg-ws-hover'>
              <div className='p-2'>
                <PhoneModeToggle
                  phoneMode={phoneMode}
                  onPhoneModeChange={onPhoneModeChange}
                  editModeLabel={editModeLabel}
                  testModeLabel={testModeLabel}
                  onSwitchToTest={() => setIsEditingFeedback(false)}
                  compact
                />
              </div>
              <div className='flex min-h-0 flex-1 flex-col'>
                <TestBar {...testBar!} gradingMode={gradingMode} compact constrainedHeight renderSection='legend' />
              </div>
            </div>
          </div>
          <TestBar {...testBar!} gradingMode={gradingMode} renderSection='controls' />
        </>
      ) : hasPhoneTestToggle && phoneMode === 'test' ? (
        // Mirrors CollapsedInputBlock's own isSelected case: the interactive preview as the main
        // content, the same shared TestBar pinned below it, and nothing else from the editing
        // chrome below (warning/answer bars, settings drawer, grading-mode/feedback bottom bar) —
        // none of that applies to a live test run. TestBar is capped to max-h-[40vh] (rather than
        // left to grow with however many chips a block has) so a block with many targeted-feedback
        // states can't squeeze testSlot down to nothing — its legend/notices scroll internally
        // instead (see the component's own comment in collapsed-input-block.tsx).
        <>
          <div className='flex-1 overflow-y-auto p-4'>
            {testSlot}
          </div>
          <div className='flex max-h-[40vh] shrink-0 flex-col'>
            <TestBar {...testBar!} gradingMode={gradingMode} constrainedHeight />
          </div>
        </>
      ) : fullScreen && landscapeLayout ? (
        // Phone-landscape 3-column edit layout — see the prop's own doc comment. Left/middle/right
        // are independent overflow-y-auto columns (not one shared scroll container), so none of
        // them need the portrait layout's `sticky` tricks to stay anchored.
        <>
        <div className='flex flex-1 overflow-hidden'>
          {/* Left rail — the same headless toolbar instance the portrait layout renders (pure
              state via sharedToolbar, no positional coupling — safe to relocate), just stacked
              vertically instead of two horizontal rows. */}
          {/* w-24 (not w-28) — narrowed slightly to free up a bit more width for the Options
              flyout/symbols popup that opens beside this rail, per the user's own suggestion. */}
          <div className='w-24 shrink-0 overflow-y-auto border-r border-ws-border bg-ws-surface p-1.5'>
            <RichTextEditor
              content=''
              onChange={() => {}}
              hideContent
              targetEditor={sharedToolbar.activeEditor}
              toolbarVisible={!!sharedToolbar.activeEditor}
              toolbarLayout='vertical'
            />
          </div>

          {/* Middle column — question/answer-setup content plus its own warning/answer bars,
              scoped to this column's width rather than the full screen. Same relative order as
              portrait: warnings above the answer bar, since a setup problem is more urgent than a
              positive "here's the current answer" summary. */}
          <div className='flex min-h-0 flex-1 flex-col overflow-hidden'>
            <div className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
              {isEditingFeedback ? (
                <>
                  {/* Same frame-owned sticky box as the portrait/desktop branch. There is no shared
                      toolbar to share it with here (it is a vertical rail in this layout), but the
                      pinning still belongs to one owner rather than to each block's own bar. */}
                  {feedbackBeforeSlot && <div className='sticky top-0 z-20'>{feedbackBeforeSlot}</div>}
                  <FeedbackPanel gradingMode={gradingMode} {...panelProps} />
                </>
              ) : (
                editingSlot
              )}
            </div>
            {/* Shown regardless of isEditingFeedback — a blank question, an incomplete answer setup,
                a coverage gap, etc. don't stop mattering just because a teacher switched to the
                Feedback tab. EVERY issue appears here, with no exceptions: `missing-feedback-text`
                used to be filtered out on the grounds that each blank card shows its own indicator,
                but the cards only exist on the Feedback tab — this bar renders on both, so the
                exclusion hid the warning precisely when nothing else was showing it. It also made
                that one issue behave unlike overlap/subsumed, which have always double-reported to
                both the bar and their card. */}
            {warningBarSlot && (
              <div className='border-t border-ws-border'>
                {warningBarSlot}
              </div>
            )}
            {scenarioBarSlot && (
              <div className='border-t border-ws-border'>
                {scenarioBarSlot}
              </div>
            )}
            {!isEditingFeedback && answerBarSlot && (
              <div className='border-t border-ws-border'>
                {answerBarSlot}
              </div>
            )}
          </div>

          {/* Right rail — Edit/Test toggle (moved here from its own full-width row above to
              reclaim vertical space — see the row's own comment), then Feedback alone at the top;
              Options and the grading-mode cycle are grouped together at the bottom (Options
              directly above Has Correct, not below Feedback), pushed down by a flex-1 spacer
              between Feedback and Options. Options is a flyout instead of BlockEditorDrawer's
              inline push-down, which would recreate the vertical squeeze this layout exists to
              avoid the moment it opened. This exact grouping/ordering is what the user asked for
              directly. */}
          <div className='flex w-40 shrink-0 flex-col gap-2 overflow-y-auto border-l border-ws-border bg-ws-hover p-2'>
            {hasPhoneTestToggle && (
              <PhoneModeToggle
                phoneMode={phoneMode}
                onPhoneModeChange={onPhoneModeChange}
                editModeLabel={editModeLabel}
                testModeLabel={testModeLabel}
                onSwitchToTest={() => setIsEditingFeedback(false)}
                compact
              />
            )}

            <button
              type='button'
              onClick={toggleFeedback}
              aria-pressed={isEditingFeedback}
              className={`flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                isEditingFeedback
                  ? 'border-ws-btn-active-fg bg-ws-btn-active text-ws-btn-active-fg'
                  : 'border-ws-border bg-ws-btn text-ws-text hover:border-ws-btn-active-fg hover:text-ws-btn-active-fg'
              }`}
            >
              <MessageSquare aria-hidden='true' className='h-4 w-4 shrink-0' />
              <span className='truncate'>{feedbackLabel}</span>
            </button>

            <div className='flex-1' />

            {settingsSlot && (
              <button
                ref={settingsToggleRef}
                type='button'
                // Marks the control that owns the surface, the way `data-reveal-surface` marks the
                // surface. Its accessible name is per-block and translated ("MCQ options",
                // "Opções de Numérico"), so anything asking whether the tray is open needs
                // something stabler than the words.
                data-reveal-toggle='settings'
                onClick={() => setSettingsOpen(v => !v)}
                aria-expanded={settingsOpen}
                aria-label={settingsA11yLabel}
                className={`flex w-full shrink-0 items-center justify-between gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                  settingsOpen
                    ? 'border-ws-in-progress-fg bg-ws-in-progress-bg/40 text-ws-in-progress-fg'
                    : 'border-ws-border bg-ws-btn text-ws-text hover:border-ws-primary hover:text-ws-primary'
                }`}
              >
                <span className='truncate'>{settingsLabel ?? settingsA11yLabel}</span>
                {settingsOpen
                  ? <ChevronDown aria-hidden='true' className='h-4 w-4 shrink-0' />
                  : <ChevronUp aria-hidden='true' className='h-4 w-4 shrink-0' />}
              </button>
            )}

            <Button onClick={onGradingModeCycle} data-field-id='gradingMode' aria-label={gradingModeCycleA11y} className='w-full shrink-0'>
              <GradingModeIcon aria-hidden='true' className='h-4 w-4 shrink-0' />
              <span className='truncate'>{gradingModeLabel}</span>
              {recallWarning}
            </Button>
          </div>
        </div>
        {/*
          * MOUNTED EVEN WHEN SHUT, which is not a detail — it is what makes undo work here.
          *
          * `pulseFirstField` does not hunt: it finds the field, reads the surface that owns it
          * off `data-reveal-surface`, and opens exactly that one. Every other thing that hides a
          * field keeps it mounted, so that holds — except this, which rendered nothing at all
          * while shut. Undoing a change to a landscape-only option therefore found the field
          * "nowhere in the document", fell through to the speculative feedback-panel branch, and
          * gave up: the whole block flashed instead of the control that changed.
          *
          * So it follows `BlockEditorDrawer`'s contract instead — present, `display: none`, and
          * `inert` — and carries the same two markers the drawer does. `display: none` is what
          * the ladder wants: `querySelector` still finds the field, `isReachable` says no, and
          * the surface named on the ancestor is opened through the registered revealer.
          */}
        {settingsSlot && createPortal(
          <div
            ref={settingsDrawerRef}
            role='dialog'
            aria-label={settingsLabel ?? settingsA11yLabel}
            aria-modal='false'
            data-reveal-surface='settings'
            data-reveal-block={blockId}
            {...(!settingsOpen ? { inert: '' } : {})}
            aria-hidden={!settingsOpen}
            // Geometry only applies once there IS geometry: until the trigger has been measured
            // the panel is shut and hidden, so it has nowhere to be.
            style={settingsOpen && settingsFlyoutPos
              ? { position: 'fixed', ...('top' in settingsFlyoutPos ? { top: settingsFlyoutPos.top } : { bottom: settingsFlyoutPos.bottom }), right: settingsFlyoutPos.right, maxHeight: settingsFlyoutPos.maxHeight, ...(settingsTwoColumn ? { width: Math.min(settingsTwoColumn.width, settingsFlyoutPos.maxWidth) } : {}), zIndex: 9999 }
              : { display: 'none' }}
            // Borrows BlockEditorDrawer's spacing/border language (border-ws-border-strong,
            // px-4 py-3.5, flex-col gap-3.5) — settingsSlot's own content (a section label +
            // rows) has no spacing of its own, it relies on this wrapper to provide it, same as
            // BlockEditorDrawer's inner div does for the portrait/desktop case. Confirmed live:
            // without this, the label/checkbox/input sat flush against each other ("smashed").
            // Background is deliberately OPAQUE (bg-ws-elevated), NOT the drawer's own
            // bg-ws-in-progress-bg/40 — that 40%-alpha tint is fine for an inline drawer that
            // never sits on top of other content, but this is a floating overlay above the
            // editor's own middle column; confirmed live the translucent version let that
            // content visibly bleed through underneath it. Kept rounded + shadow-lg (unlike the
            // drawer) since this is a floating popover, not an inline push-down panel.
            //
            // Escape-to-close is handled by the document-level keydown listener in the
            // settingsOpen effect above (not a local onKeyDown here) — this flyout never moves
            // focus into itself on open, so focus normally stays on the toggle button, which
            // lives in an entirely separate DOM subtree from this portaled div; a listener scoped
            // to this element would never see that keystroke.
            // `overflow-y-auto` is the other half of the `maxHeight` above: the cap decides how much
            // room the panel may take, this decides what happens to the rest of it. Without the
            // scroller the cap would only move the loss from off-screen to clipped — and with two
            // columns below it is the floor rather than the usual case, since a panel that still
            // does not fit in two columns has to go somewhere.
            className={`overflow-y-auto rounded-lg border border-ws-border-strong bg-ws-elevated px-4 py-3.5 shadow-lg${settingsTwoColumn ? '' : ' w-72'}`}
          >
            {/* The LAYOUT is the container's, not the sections' — see `OptionsSection`. One
                column draws the rule between sections; two columns let the gutter do it, because
                a border-top on a section that happens to start a column is a line across the top
                of nothing. */}
            <div className={settingsTwoColumn ? OPTIONS_PANEL_TWO_COLUMN : OPTIONS_PANEL_SINGLE_COLUMN}>
              {settingsSlot}
            </div>
          </div>,
          document.body
        )}
        </>
      ) : (
        <>
          {/* Main content — feedback panel or editing view; flex-1 keeps bottom bar anchored.
              This div is NOT a flex container (a plain block with flex-1 only sizing it as an
              item within the frame's own flex column above) — so the shared toolbar box below
              must be `sticky`, not a plain non-scrolling sibling, to actually stay anchored while
              its content scrolls; a flex-1/overflow-hidden split here would silently no-op (the
              split's flex-1 has nothing to size against) and the whole thing — toolbar included —
              would just scroll away with this div's own overflow-y-auto.

              `min-h-0` is load-bearing and its absence was a latent bug. A flex item defaults to
              `min-height: auto`, which is its CONTENT height — so this div refused to shrink below
              whatever the feedback list happened to be, pushed the card past its own height, and
              the card (which is itself `overflow-y-auto`) started scrolling as a whole. Two nested
              scrollers, and the outer one takes the toolbar, the scenario bar and the bottom bar
              with it. It only became reachable once the panel grew tall enough to exceed the card,
              which is why it looked new. */}
          <div className='flex min-h-0 flex-1 flex-col overflow-y-auto'>
            {/* ONE sticky box for both bars. Kept as a single box rather than two siblings each at
                `top-0`: the toolbar's height changes with whether an editor is focused, so any
                offset the second bar hard-coded would be wrong half the time. */}
            <div className='sticky top-0 z-20'>
              <AnchoredStylingBar toolbar={sharedToolbar} fullScreen={fullScreen} />
              {isEditingFeedback && feedbackBeforeSlot}
            </div>
            {isEditingFeedback ? (
              <FeedbackPanel gradingMode={gradingMode} {...panelProps} />
            ) : (
              editingSlot
            )}
          </div>

          {/* Warning bar — sticky between editing content and the answer bar (if any). Shown
              regardless of feedback mode: a blank question or an incomplete answer setup doesn't
              stop mattering just because a teacher switched to the Feedback tab.
              `missing-feedback-text` is the one issue that's genuinely feedback-specific, and it's
              already excluded from every block's own `editorIssuesForBar` (each blank feedback
              card shows its own indicator instead), so nothing duplicates. Ordered above the
              answer bar — a setup problem is more urgent than "here's the current correct answer,"
              so it should be the first thing seen when both are present at once. */}
          {warningBarSlot && (
            <div className='border-t border-ws-border'>
              {warningBarSlot}
            </div>
          )}

          {/* Scenario bar — above the answer bar and shown on both tabs, so the control that
              decides what every number on screen resolves to does not vanish on the tab whose
              content is nothing but numbers. */}
          {scenarioBarSlot && (
            <div className='border-t border-ws-border'>
              {scenarioBarSlot}
            </div>
          )}

          {/* Answer bar — sticky between editing content and bottom bar; hidden in feedback mode */}
          {!isEditingFeedback && answerBarSlot && (
            <div className='border-t border-ws-border'>
              {answerBarSlot}
            </div>
          )}

          {/* Settings drawer */}
          {settingsSlot && (
            /* `data-reveal-surface` lets the reveal ladder work out WHICH surface is hiding a
               field, instead of opening each one in turn to find out. The drawer keeps its
               contents mounted while shut, so the field can be found and its owner read off the
               tree — no guessing, and nothing opens that does not need to. */
            <div ref={settingsDrawerRef} data-reveal-surface='settings' data-reveal-block={blockId}>
              <BlockEditorDrawer open={settingsOpen}>
                {/* Always one column here: the drawer is a narrow push-down inside a column that
                    already scrolls, so it has no height problem to solve and no width to spend. */}
                <div className={OPTIONS_PANEL_SINGLE_COLUMN}>
                  {settingsSlot}
                </div>
              </BlockEditorDrawer>
            </div>
          )}

          {/* Bottom bar */}
          <div className={`relative flex items-center border-t border-ws-border bg-ws-hover px-4 py-2${fullScreen ? '' : ' rounded-b-xl'}`}>
            {/* Grading mode cycle */}
            <Button onClick={onGradingModeCycle} data-field-id='gradingMode' aria-label={gradingModeCycleA11y}>
              <GradingModeIcon aria-hidden='true' className='h-4 w-4' />
              {gradingModeLabel}
              {recallWarning}
            </Button>

            <div className='flex-1' />

            {/* Settings toggle — centered, only shown if settingsSlot is provided */}
            {settingsSlot && (
              <button
                ref={settingsToggleRef}
                type='button'
                // Marks the control that owns the surface, the way `data-reveal-surface` marks the
                // surface. Its accessible name is per-block and translated ("MCQ options",
                // "Opções de Numérico"), so anything asking whether the tray is open needs
                // something stabler than the words.
                data-reveal-toggle='settings'
                onClick={() => setSettingsOpen(v => !v)}
                aria-expanded={settingsOpen}
                aria-label={settingsA11yLabel}
                className={`absolute left-1/2 -translate-x-1/2 flex h-7 w-7 items-center justify-center rounded-md transition-colors ${settingsOpen ? 'bg-ws-in-progress-bg/40 text-ws-in-progress-fg' : 'text-ws-muted hover:bg-black/10 hover:text-ws-text dark:hover:bg-white/10'}`}
              >
                {settingsOpen
                  ? <ChevronDown aria-hidden='true' className='h-4 w-4' />
                  : <ChevronUp aria-hidden='true' className='h-4 w-4' />}
              </button>
            )}

            {/* Feedback toggle */}
            <button
              type='button'
              onClick={toggleFeedback}
              aria-pressed={isEditingFeedback}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                isEditingFeedback
                  ? 'border-ws-btn-active-fg bg-ws-btn-active text-ws-btn-active-fg'
                  : 'border-ws-border bg-ws-btn text-ws-text hover:border-ws-btn-active-fg hover:text-ws-btn-active-fg'
              }`}
            >
              <MessageSquare aria-hidden='true' className='h-4 w-4' />
              {feedbackLabel}
            </button>
          </div>
        </>
      )}

      {toastSlot}
    </div>
  );

  /*
   * EVERY FORMULA FIELD IN THIS BLOCK, wherever it sits.
   *
   * Here rather than in each block because context resolves by tree POSITION, not by where the
   * element was created: a block builds its `editingSlot` and its feedback cards itself, but
   * they render inside this frame, so one provider reaches all of them. That is what makes the
   * behaviour identical across every equation input — the resolution, the precedence, the
   * default and the control itself have exactly one implementation.
   *
   * What a block still supplies is the irreducible part: its own map and its own way of writing
   * one, since no two of them store their data the same way.
   */
  const scoped = (
    <AngleModeProvider modes={angleModes} fallback={angleModeFallback} onChange={onAngleModeChange ?? NO_ANGLE_WRITE}>
      {frame}
    </AngleModeProvider>
  );
  return fullScreen ? createPortal(scoped, document.body) : scoped;
}
